import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { siteFirewall } from "../../api/endpoints/siteFirewall";
import { qk } from "../../api/queryKeys";
import type {
  FirewallRule,
  RuleCreateIn,
  RuleFamily,
  RuleProtocol,
} from "../../api/types/siteFirewall";
import { FormShell } from "../../components/forms/FormShell";
import { TextField } from "../../components/forms/TextField";
import { SelectField } from "../../components/forms/SelectField";
import { useSiteVlanNames } from "./useSiteVlanNames";

// Sentinels for the two "null means something" selects. `to_vlan_name: null` on
// an outbound rule = "to WAN/internet"; `protocol: null` = "any".
const WAN = "__wan__";
const ANY = "__any__";

const NAME_RE = /^[a-z0-9][a-z0-9_-]*$/;

const FAMILY_OPTIONS = [
  { value: "inter_vlan", label: "Inter-VLAN" },
  { value: "outbound", label: "Outbound (internet)" },
] as const;

const PROTOCOL_OPTIONS = [
  { value: ANY, label: "Any" },
  { value: "tcp", label: "TCP" },
  { value: "udp", label: "UDP" },
  { value: "icmp", label: "ICMP" },
] as const;

interface RuleDraft {
  family: RuleFamily;
  name: string;
  from_vlan_name: string;
  to_vlan_name: string; // "" | WAN | <name>
  protocol: string; // ANY | tcp | udp | icmp
  dst_ports_raw: string;
  notes: string;
}

function toDraft(rule?: FirewallRule): RuleDraft {
  if (!rule) {
    return {
      family: "inter_vlan",
      name: "",
      from_vlan_name: "",
      to_vlan_name: "",
      protocol: ANY,
      dst_ports_raw: "",
      notes: "",
    };
  }
  return {
    family: rule.family,
    name: rule.name,
    from_vlan_name: rule.from_vlan_name,
    to_vlan_name: rule.to_vlan_name ?? (rule.family === "outbound" ? WAN : ""),
    protocol: rule.protocol ?? ANY,
    dst_ports_raw: rule.dst_ports.join(", "),
    notes: rule.notes ?? "",
  };
}

function parsePorts(raw: string): { ok: boolean; value: number[]; error: string | null } {
  const parts = raw
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const value: number[] = [];
  for (const p of parts) {
    const n = Number(p);
    if (!Number.isInteger(n) || n < 1 || n > 65535) {
      return { ok: false, value: [], error: `Invalid port "${p}" (1–65535).` };
    }
    value.push(n);
  }
  return { ok: true, value, error: null };
}

export function FirewallRuleForm({
  accountSlug,
  siteSlug,
  rule,
  canWrite,
  onClose,
}: {
  accountSlug: string;
  siteSlug: string;
  rule?: FirewallRule;
  canWrite: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const vlan = useSiteVlanNames(accountSlug, siteSlug);
  const [draft, setDraft] = useState<RuleDraft>(() => toDraft(rule));
  const set = <K extends keyof RuleDraft>(k: K, v: RuleDraft[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const onFamilyChange = (f: RuleFamily) =>
    setDraft((d) => {
      let to = d.to_vlan_name;
      if (f === "inter_vlan" && to === WAN) to = ""; // WAN invalid for inter-VLAN
      if (f === "outbound" && to === "") to = WAN; // default outbound → WAN
      return { ...d, family: f, to_vlan_name: to };
    });

  const mutation = useMutation({
    mutationFn: (body: RuleCreateIn) =>
      rule
        ? siteFirewall.updateRule(siteSlug, rule.id, body)
        : siteFirewall.createRule(siteSlug, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.siteFirewallRules(siteSlug) });
      void qc.invalidateQueries({ queryKey: qk.siteFirewallPolicy(siteSlug) });
      onClose();
    },
  });

  const disabled = !canWrite || mutation.isPending;
  const ports = parsePorts(draft.dst_ports_raw);

  const nameError =
    draft.name === ""
      ? "Required."
      : NAME_RE.test(draft.name)
        ? null
        : "Lowercase letter/digit, then [a-z0-9_-].";
  const fromError = draft.from_vlan_name === "" ? "Required." : null;
  const toIsWan = draft.to_vlan_name === WAN;
  const toResolved = toIsWan || draft.to_vlan_name === "" ? null : draft.to_vlan_name;
  const toError =
    draft.family === "inter_vlan" && toResolved === null
      ? "Inter-VLAN rule needs a destination VLAN."
      : toResolved !== null && toResolved === draft.from_vlan_name
        ? "Destination must differ from source."
        : null;

  const isValid =
    !nameError && !fromError && !toError && ports.ok && vlan.names.length > 0;

  const toOptions =
    draft.family === "outbound"
      ? [{ value: WAN, label: "WAN / internet (none)" }, ...vlan.names.map((n) => ({ value: n, label: n }))]
      : vlan.names.map((n) => ({ value: n, label: n }));

  return (
    <FormShell
      submitLabel={rule ? "Save rule" : "Add rule"}
      onSubmit={(e) => {
        e.preventDefault();
        if (!isValid || disabled) return;
        mutation.mutate({
          family: draft.family,
          name: draft.name.trim(),
          from_vlan_name: draft.from_vlan_name,
          to_vlan_name: toResolved,
          protocol: draft.protocol === ANY ? null : (draft.protocol as RuleProtocol),
          dst_ports: ports.value,
          notes: draft.notes.trim() === "" ? null : draft.notes.trim(),
        });
      }}
      onCancel={onClose}
      pending={mutation.isPending}
      submitDisabled={!canWrite || !isValid}
      error={mutation.error}
      errorTitle="Firewall rule save failed"
    >
      {vlan.names.length === 0 && (
        <p style={{ color: "var(--warn)", fontSize: "0.85rem" }}>
          No generated VLANs for this site yet — allocate the site's VLANs first.
        </p>
      )}
      <SelectField
        label="Family"
        value={draft.family}
        onChange={(v) => onFamilyChange(v as RuleFamily)}
        options={FAMILY_OPTIONS}
        disabled={disabled}
      />
      <TextField
        label="Name"
        value={draft.name}
        onChange={(v) => set("name", v)}
        placeholder="trusted-lan-to-homeassistant"
        error={nameError}
        disabled={disabled}
      />
      <SelectField
        label="From VLAN"
        value={draft.from_vlan_name}
        onChange={(v) => set("from_vlan_name", v)}
        options={vlan.names.map((n) => ({ value: n, label: n }))}
        placeholder="Select VLAN"
        error={fromError}
        disabled={disabled}
      />
      <SelectField
        label="To VLAN"
        value={draft.to_vlan_name}
        onChange={(v) => set("to_vlan_name", v)}
        options={toOptions}
        placeholder="Select VLAN"
        helper={draft.family === "outbound" ? "Pick WAN for plain internet egress." : undefined}
        error={toError}
        disabled={disabled}
      />
      <SelectField
        label="Protocol"
        value={draft.protocol}
        onChange={(v) => set("protocol", v)}
        options={PROTOCOL_OPTIONS}
        disabled={disabled}
      />
      <TextField
        label="Destination ports"
        value={draft.dst_ports_raw}
        onChange={(v) => set("dst_ports_raw", v)}
        placeholder="8123, 554"
        helper="Comma/space separated (1–65535). Leave blank for all ports."
        error={ports.error}
        disabled={disabled}
      />
      <TextField
        label="Notes"
        value={draft.notes}
        onChange={(v) => set("notes", v)}
        disabled={disabled}
      />
    </FormShell>
  );
}
